import { Button, Icon, RichTextEditor } from "@legal-500/legal500-design-system";
import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
  type UseFormRegister,
  type UseFormWatch,
} from "react-hook-form";
import { blankRichText } from "./blockContent";
import { BLOCK_BACKGROUNDS, type BlockBackground } from "./types";
import styles from "../CMS.module.css";

export const HREF_PATTERN = String.raw`(https?://|/(?!/|\\)|mailto:|#).*`;
export const HREF_TITLE = "Enter a full URL (https://…), a path starting with /, or a mailto: link";

export function useSyncBlockContent<T extends FieldValues>(
  watch: UseFormWatch<T>,
  value: T,
  onChange: (value: T) => void,
) {
  useEffect(() => {
    const subscription = watch(formValues => onChange({ ...value, ...(formValues as T) }));
    return () => subscription.unsubscribe();
  }, [watch, onChange, value]);
}

export function useUploadTracker(onUploadingChange?: (uploading: boolean) => void) {
  const uploads = useRef(new Set<string>());
  const [uploading, setUploading] = useState(false);
  function report(key: string, pending: boolean) {
    if (pending) uploads.current.add(key);
    else uploads.current.delete(key);
    setUploading(uploads.current.size > 0);
    onUploadingChange?.(uploads.current.size > 0);
  }
  return { uploading, report };
}

const BACKGROUND_LABELS: Record<BlockBackground, string> = {
  default: "White",
  muted: "Grey",
  blue: "Blue",
  dark: "Dark",
  brand: "Brand",
};

const DEFAULT_BACKGROUND_OPTIONS: readonly BlockBackground[] = ["default", "muted"];

type BackgroundFieldProps<T extends FieldValues> = {
  register: UseFormRegister<T>;
  name: FieldPath<T>;
  stored?: BlockBackground;
  options?: readonly BlockBackground[];
};

export function BackgroundField<T extends FieldValues>({
  register,
  name,
  stored,
  options = DEFAULT_BACKGROUND_OPTIONS,
}: BackgroundFieldProps<T>) {
  const [initial] = useState(stored);
  const offered = BLOCK_BACKGROUNDS.filter(
    background => options.includes(background) || background === initial,
  );
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel} htmlFor="block-background">
        Background
      </label>
      <select id="block-background" className={styles.selectField} {...register(name)}>
        {offered.map(background => (
          <option key={background} value={background}>
            {BACKGROUND_LABELS[background]}
          </option>
        ))}
      </select>
    </div>
  );
}

type RichTextFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  id: string;
  label: string;
  minHeight?: number;
};

export function RichTextField<T extends FieldValues>({
  control,
  name,
  id,
  label,
  minHeight = 160,
  emptyAsBlank = false,
}: RichTextFieldProps<T> & { emptyAsBlank?: boolean }) {
  return (
    <div className={styles.formField}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <RichTextEditor
            id={id}
            value={field.value ?? ""}
            onChange={html => field.onChange(emptyAsBlank ? blankRichText(html) : html)}
            preset="full"
            headingIds
            htmlTab
            label={label}
            minHeight={minHeight}
          />
        )}
      />
    </div>
  );
}

type BlockItemListProps = {
  noun: string;
  keys: string[];
  disabled?: boolean;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
  renderItem: (index: number, key: string) => ReactNode;
};

export function BlockItemList({
  noun,
  keys,
  disabled = false,
  onMove,
  onRemove,
  onAdd,
  renderItem,
}: BlockItemListProps) {
  const lower = noun.toLowerCase();
  return (
    <div className={styles.cardEditorList}>
      {keys.map((key, index) => (
        <div key={key} className={styles.cardEditorItem}>
          <div className={styles.cardEditorItemHeader}>
            <span className={styles.cardEditorItemTitle}>
              {noun} {index + 1}
            </span>
            <div className={styles.cardEditorItemActions}>
              <Button
                variant="outlined"
                color="default"
                onClick={() => onMove(index, index - 1)}
                disabled={disabled || index === 0}
                aria-label={`Move ${lower} ${index + 1} up`}
              >
                <Icon icon="chevron-up" size="small" />
              </Button>
              <Button
                variant="outlined"
                color="default"
                onClick={() => onMove(index, index + 1)}
                disabled={disabled || index === keys.length - 1}
                aria-label={`Move ${lower} ${index + 1} down`}
              >
                <Icon icon="chevron-down" size="small" />
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => onRemove(index)}
                disabled={disabled}
                aria-label={`Delete ${lower} ${index + 1}`}
              >
                <Icon icon="delete" size="small" />
              </Button>
            </div>
          </div>
          <div className={styles.cardEditorFields}>{renderItem(index, key)}</div>
        </div>
      ))}

      <Button type="button" variant="outlined" color="primary" onClick={onAdd} disabled={disabled}>
        <Icon icon="plus" size="small" />
        Add {noun}
      </Button>
    </div>
  );
}
